import { useState } from 'react';
import { ChevronRight, Download, Eye } from 'lucide-react';

import { downloadArtefato, previewArtefato } from '@/api/client';
import type { DocumentoDoAtendimento, InstrumentoConversa } from '@/api/secretario';
import { DocumentPreviewDialog, type PreviewableDocument } from '@/components/DocumentPreviewDialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useNotification } from '@/context/NotificationProvider';
import { notifyMutationError } from '@/lib/errors';

import { classesDoTom, quando } from './util';

const STATUS_INSTRUMENTO: Record<string, string> = {
  RASCUNHO: 'Não enviado',
  AGUARDANDO_REVISAO: 'Aguarda a sua revisão',
  AGUARDANDO_ASSINATURA: 'Aguarda o cliente',
  ASSINADO_PARCIAL: 'Entregue em parte',
  // "Assinado" afirmaria uma conferência de assinatura que ninguém fez; "entregue" diz só o que houve.
  ASSINADO: 'Entregue pelo cliente',
  RECUSADO: 'Recusado',
  EXPIRADO: 'Expirado',
};

/**
 * A conferência por conteúdo (o texto do arquivo entregue comparado com o que foi
 * enviado). Só aparece quando houve: ou tranquiliza, ou pede "abra e confira".
 */
const CONFERENCIA: Record<string, { texto: string; atencao: boolean }> = {
  CONFIRMADO: { texto: 'texto confere com o original', atencao: false },
  INDETERMINADO: { texto: 'não foi possível ler o texto; abra e confira', atencao: true },
  RECUSADO: { texto: 'texto diferente do original; abra e confira', atencao: true },
};

interface Arquivo {
  artefatoId: string;
  nome: string;
  mime: string;
}

function podeVisualizar(mime: string): boolean {
  return mime === 'application/pdf' || mime.startsWith('image/');
}

function capitalizar(texto: string): string {
  return texto.charAt(0).toUpperCase() + texto.slice(1);
}

/**
 * O que o cliente entregou: contratação, qualificação e os documentos do caso
 * (colapsados). Cada linha diz o que é, em que situação está e abre o arquivo
 * dentro do app; nada de rótulo que o advogado não saberia o que fazer com ele.
 */
export function Entregas({ instrumentos, documentos }: { instrumentos: InstrumentoConversa[]; documentos: DocumentoDoAtendimento[] }) {
  const { showError } = useNotification();
  const [preview, setPreview] = useState<{ doc: PreviewableDocument; url: string } | null>(null);
  const [doCasoAbertos, setDoCasoAbertos] = useState(false);
  const qualificacao = documentos.filter((d) => d.qualificacao);
  const doCaso = documentos.filter((d) => !d.qualificacao);

  const ver = async (arquivo: Arquivo) => {
    try {
      if (!podeVisualizar(arquivo.mime)) {
        await baixar(arquivo);
        return;
      }
      const { download_url } = await previewArtefato(arquivo.artefatoId);
      setPreview({ doc: { id: arquivo.artefatoId, nome: arquivo.nome, mime: arquivo.mime }, url: download_url });
    } catch (err) {
      notifyMutationError(err, showError);
    }
  };
  const baixar = async (arquivo: Arquivo) => {
    try {
      const { download_url } = await downloadArtefato(arquivo.artefatoId);
      const a = document.createElement('a');
      a.href = download_url;
      a.download = arquivo.nome;
      a.click();
    } catch (err) {
      notifyMutationError(err, showError);
    }
  };

  return (
    <Card>
      <CardContent className="space-y-4 p-4">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">O que o cliente entregou</h2>

        <section className="space-y-2">
          <h3 className="text-xs text-muted-foreground">Contratação</h3>
          {instrumentos.length === 0 ? (
            <p className="text-sm text-muted-foreground">Nenhum documento de contratação enviado ainda.</p>
          ) : (
            <ul className="divide-y">
              {instrumentos.map((i) => (
                <InstrumentoLinha key={i.id} instrumento={i} onVer={ver} />
              ))}
            </ul>
          )}
        </section>

        <section className="space-y-2">
          <h3 className="text-xs text-muted-foreground">Qualificação</h3>
          {qualificacao.length === 0 ? (
            <p className="text-sm text-muted-foreground">Sem itens de qualificação no checklist.</p>
          ) : (
            <ul className="divide-y">
              {qualificacao.map((d, i) => (
                <DocumentoLinha key={`${d.item_nome}-${i}`} documento={d} onVer={ver} />
              ))}
            </ul>
          )}
        </section>

        {doCaso.length > 0 && (
          <section className="space-y-2">
            <Button
              type="button"
              variant="link"
              className="h-auto min-h-11 px-0 text-sm sm:min-h-0"
              aria-expanded={doCasoAbertos}
              onClick={() => setDoCasoAbertos((v) => !v)}
            >
              <ChevronRight className={`mr-1 h-4 w-4 transition-transform ${doCasoAbertos ? 'rotate-90' : ''}`} aria-hidden="true" />
              Documentos do caso ({doCaso.length})
            </Button>
            {doCasoAbertos && (
              <ul className="divide-y">
                {doCaso.map((d, i) => (
                  <DocumentoLinha key={`${d.item_nome}-${i}`} documento={d} onVer={ver} />
                ))}
              </ul>
            )}
          </section>
        )}
      </CardContent>

      <DocumentPreviewDialog
        open={preview !== null}
        onOpenChange={(open) => !open && setPreview(null)}
        document={preview?.doc ?? null}
        previewUrl={preview?.url ?? ''}
        onDownload={() => preview && baixar({ artefatoId: preview.doc.id, nome: preview.doc.nome, mime: preview.doc.mime })}
      />
    </Card>
  );
}

function InstrumentoLinha({ instrumento, onVer }: { instrumento: InstrumentoConversa; onVer: (a: Arquivo) => void }) {
  const entregue = instrumento.status === 'ASSINADO';
  const titulo = instrumento.titulo ? capitalizar(instrumento.titulo) : instrumento.tipo;
  const arquivos = instrumento.arquivos.filter((a) => a.artefato_id);
  const conferencia = instrumento.conferencia ? CONFERENCIA[instrumento.conferencia] : undefined;
  const fotos = instrumento.como_voltou && instrumento.como_voltou !== 'PDF' && arquivos.length > 0 ? arquivos.length : 0;
  const trilha = [
    instrumento.enviado_em ? `enviado ${quando(instrumento.enviado_em)}` : null,
    instrumento.recebido_em ? `entregue ${quando(instrumento.recebido_em)}` : null,
    // Só as fotos pedem algo (conferir se dá para ler); "em PDF" não dizia nada.
    fotos > 0 ? `em ${fotos} ${fotos === 1 ? 'foto' : 'fotos'}` : null,
  ].filter((parte): parte is string => parte !== null);

  return (
    <li className="space-y-1.5 py-2.5 first:pt-0 last:pb-0">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-sm font-medium">{titulo}</span>
        <Badge variant="outline" className={entregue ? classesDoTom('neutral') : classesDoTom('warn')}>
          {STATUS_INSTRUMENTO[instrumento.status] ?? instrumento.status}
        </Badge>
      </div>
      {(trilha.length > 0 || conferencia) && (
        <p className="text-xs text-muted-foreground tabular-nums">
          {trilha.length > 0 && capitalizar(trilha.join(' · '))}
          {conferencia && (
            <span className={conferencia.atencao ? 'font-medium text-amber-700 dark:text-amber-300' : ''}>
              {trilha.length > 0 ? ' · ' : ''}
              {trilha.length > 0 ? conferencia.texto : capitalizar(conferencia.texto)}
            </span>
          )}
        </p>
      )}
      <div className="flex flex-wrap gap-2">
        {arquivos.map((a, i) => (
          <Button
            key={a.hash}
            type="button"
            size="sm"
            variant="outline"
            className="h-11 sm:h-9"
            onClick={() => onVer({ artefatoId: a.artefato_id!, nome: a.nome, mime: a.mime ?? 'application/pdf' })}
          >
            <Eye className="mr-1.5 h-4 w-4" aria-hidden="true" />
            {arquivos.length > 1 ? `Ver ${i + 1}` : 'Ver'}
          </Button>
        ))}
        {arquivos.length === 0 && instrumento.artefato_assinado_id && (
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="h-11 sm:h-9"
            onClick={() => onVer({ artefatoId: instrumento.artefato_assinado_id!, nome: `${titulo} entregue.pdf`, mime: 'application/pdf' })}
          >
            <Eye className="mr-1.5 h-4 w-4" aria-hidden="true" />
            Ver
          </Button>
        )}
        {instrumento.artefato_id && (
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="h-11 sm:h-9"
            onClick={() => onVer({ artefatoId: instrumento.artefato_id!, nome: `${titulo}.pdf`, mime: 'application/pdf' })}
          >
            Ver original
          </Button>
        )}
      </div>
    </li>
  );
}

function DocumentoLinha({ documento, onVer }: { documento: DocumentoDoAtendimento; onVer: (a: Arquivo) => void }) {
  const tom = documento.conferido ? 'ok' : documento.status === 'NAO_ENVIADO' || documento.status === 'DISPENSADO' ? 'neutral' : 'warn';
  return (
    <li className="space-y-1 py-2.5 first:pt-0 last:pb-0">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-sm font-medium">{documento.item_nome}</span>
        {/* Badge e botão na mesma altura: a linha lê como um par só. */}
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant="outline" className={`h-11 sm:h-9 ${classesDoTom(tom)}`}>
            {documento.rotulo_status}
          </Badge>
          {documento.arquivos.map((a, i) => (
            <Button
              key={a.artefato_id}
              type="button"
              size="sm"
              variant="outline"
              className="h-11 sm:h-9"
              onClick={() => onVer({ artefatoId: a.artefato_id, nome: a.nome, mime: a.mime })}
            >
              {podeVisualizar(a.mime) ? <Eye className="mr-1.5 h-4 w-4" aria-hidden="true" /> : <Download className="mr-1.5 h-4 w-4" aria-hidden="true" />}
              {documento.arquivos.length > 1 ? `Ver ${i + 1}` : 'Ver'}
            </Button>
          ))}
        </div>
      </div>
      {documento.item_condicao && !documento.conferido && <p className="text-xs text-muted-foreground">Pedido só {documento.item_condicao}.</p>}
      {documento.motivo && <p className="text-xs text-muted-foreground">“{documento.motivo}”</p>}
      {documento.campos.length > 0 && (
        <dl className="flex flex-wrap gap-x-4 gap-y-0.5 text-xs">
          {documento.campos.map((c) => (
            <div key={c.campo}>
              <dt className="inline text-muted-foreground">{c.rotulo}: </dt>
              <dd className="inline">
                {c.corrigido ? (
                  <>
                    <b className="font-medium">{c.corrigido}</b> <span className="text-muted-foreground">(lido: {c.lido})</span>
                  </>
                ) : (
                  <b className="font-medium">{c.lido}</b>
                )}
              </dd>
            </div>
          ))}
        </dl>
      )}
      {documento.substituiu_anterior && <p className="text-xs text-muted-foreground">Substituiu um envio anterior, que fica guardado.</p>}
    </li>
  );
}
